import React, { FunctionComponent, useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { Modal, Header } from 'semantic-ui-react';
import { ErrorMessage } from '@hookform/error-message';
import withStyles from 'react-jss';
import { compose } from 'recompose';
import { Storage } from 'aws-amplify';
import { useForm } from 'react-hook-form';
import classnames from 'classnames';
import { map, all, flip, includes } from 'ramda';
import DatePicker from 'react-datepicker';
import { toastMapping } from 'components/ErrorBoundary/ErrorBoundary';

import { WithRouterProps, ClassProp } from 'helpers/types';
import { DATE_FORMAT } from 'helpers/date';

import styles from './CardNetworkResponseUploadModal.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;

type FormData = {
  files: File[];
  fileType: string;
};

type Props = WithRouterProps & {
  classes: Styles;
  trigger: HTMLElement;
  title: string;
  cardNetwork: string;
  processType: string;
};

const fileName: { [key: string]: string } = {
  MASTERCARD: 'MID',
  AMEX: 'SE',
};

const readFileAsync = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onload = () => {
      resolve(fileReader.result as string);
    };

    fileReader.onerror = reject;
    fileReader.readAsText(file);
  });

const validateMastercardFile = map((line: string) => {
  const cleanedLine = line.trim();
  const firstPart = cleanedLine.split('|')[0];
  const validCodes = ['10', '20', '25', '30'];

  if (!firstPart) {
    throw new Error(`Invalid file type - line is empty.`);
  }

  if (!validCodes.includes(firstPart)) {
    throw new Error(`Invalid file type - ${firstPart} isn't a valid code.`);
  }
});

const requiredColumns = [
  'Name',
  'DBA',
  'Address 1',
  'Address 2',
  'City',
  'State',
  'ZIP',
  'Country',
];

const validateAmexFile = (keys: string[]) => {
  const valid = all<any>(flip(includes)(keys), requiredColumns);
  // incubit file's has odd parsing and the columns includes `"Access Code /` instead of `Access Code / Service Est Number (SENO)`
  const incubitValid =
    keys.includes('Access Code / Service Est Number (SENO)') ||
    keys.includes('"Access Code /');

  if (!valid || !incubitValid) {
    throw new Error(
      `Invalid file type - it should contain all required columns.`,
    );
  }
};

const getFileName = (
  isAggregateProcess: boolean,
  activationDate: Date,
  file: File,
  cardNetwork: string,
) => {
  const nameWithoutSpaces = file.name.replace(/\s/g, '_');

  if (isAggregateProcess) {
    return `${activationDate.getTime()}_${nameWithoutSpaces}`;
  } else if (cardNetwork === 'MASTERCARD') {
    const fileNameIncludesValidExtension = nameWithoutSpaces.includes(
      '.mastercard.csv',
    );
    if (!fileNameIncludesValidExtension) {
      return nameWithoutSpaces.replace('.csv', '.mastercard.csv');
    }
  }

  return nameWithoutSpaces;
};

const fileValidation = async (
  cardNetwork: string,
  file: Blob,
  setError: Function,
  clearErrors: (name?: string | string[] | undefined) => void,
) => {
  try {
    const fileContent = await readFileAsync(file);
    const formattedFileContent = fileContent
      .trim()
      .split('\n')
      .map((row: string) => row.trim().replace(/\s+/g, ' '));

    if (cardNetwork === 'MASTERCARD') {
      validateMastercardFile(formattedFileContent);
    }

    if (cardNetwork === 'AMEX') {
      const keys = formattedFileContent[0]
        .split(',')
        .map((key: string) => key.trim());

      validateAmexFile(keys);
    }

    clearErrors('files');
  } catch (err) {
    setError('files', 'notMatch', err.message);
  }
};

const CardNetworkResponseUploadModal: FunctionComponent<Props> = ({
  trigger,
  title,
  classes,
  cardNetwork,
  processType,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [fileUploaded, setFileUploaded] = useState<boolean>(false);
  const [activationDate, setActivationDate] = useState<Date>(new Date());

  const isRetrievalProcess = processType === 'Retrieval';
  const isAggregateProcess = processType === 'Aggregate';

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<FormData>();

  const [fileValue] = (watch('files') as File[]) || [null];

  const onSubmit = async (data: FormData) => {
    setLoading(true);

    const {
      files: [file],
    } = data;

    const fileName = getFileName(
      isAggregateProcess,
      activationDate,
      file,
      cardNetwork,
    );

    try {
      await Storage.put(
        `onboardingAutomation/${processType.toLowerCase()}/${cardNetwork.toLowerCase()}/response/${fileName}`,
        file,
        {
          contentType: 'text/csv',
          bucket: process.env.CN_BUCKET,
          region: process.env.AWS_REGION,
        },
      );

      setFileUploaded(true);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fileValue) {
      fileValidation(cardNetwork, fileValue, setError, clearErrors);
    }
  }, [fileValue]);

  useEffect(() => {
    if (fileUploaded) {
      toastMapping(
        {},
        'success',
      )([
        {
          message: 'File has been successufly uploaded',
          displayMessage: 'Success',
        },
      ]);
    }
  }, [fileUploaded]);

  return (
    <Modal trigger={trigger} closeIcon size="mini">
      <Header className={classes.header} icon="upload" content={title} />

      <Modal.Content>
        <form onSubmit={handleSubmit(onSubmit)}>
          {isAggregateProcess && (
            <div className={classes.fieldGroup}>
              <label className={classes.label}>Activation Date</label>
              <DatePicker
                onChange={setActivationDate}
                selected={activationDate}
                name="activationDate"
                dateFormat={DATE_FORMAT}
              />
            </div>
          )}
          <div className={classes.fieldGroup}>
            <input
              name="fileType"
              type="radio"
              value="se"
              ref={register({ required: true })}
            />
            <label className={classes.label}>
              {isRetrievalProcess ? fileName[cardNetwork] : processType} File
            </label>
          </div>

          <div className={classes.fieldGroup}>
            <input
              name="fileType"
              type="radio"
              value="refresh"
              ref={register({ required: true })}
            />
            <label className={classes.label}>Refresh File</label>
          </div>

          <div className={classes.fieldGroup}>
            <input
              className={classes.file}
              name="files"
              type="file"
              accept="text/csv"
              ref={register({ required: true })}
            />
            <ErrorMessage
              errors={errors}
              name="files"
              render={({ message }) => (
                <span className={classes.message}>{message}</span>
              )}
            ></ErrorMessage>
          </div>

          <div className={classes.fieldGroup}>
            <input
              disabled={loading || !!errors.files}
              type="submit"
              value={loading ? 'Loading...' : 'Upload'}
              className={classnames(classes.submit, {
                [classes.disabled]: loading || !!errors.files,
              })}
            />
          </div>
        </form>
      </Modal.Content>
    </Modal>
  );
};

export default compose<Props, any>(
  withRouter,
  withStyles(styles),
)(CardNetworkResponseUploadModal);
